import { useState, useEffect } from 'react'
import { VITE_API_BASE } from '../config/apiConfig'

const API = VITE_API_BASE

// 获取当前机构ID
const getCurrentInstitutionId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    return user?.institutionId || null
  } catch { return null }
}

export default function StoreSettlements() {
  const [settlements, setSettlements] = useState([])
  const [stats, setStats] = useState({})
  const [loading, setLoading] = useState(true)
  const [selectedIds, setSelectedIds] = useState([])
  const [filter, setFilter] = useState({ status: '' })
  const [institutionId] = useState(getCurrentInstitutionId)
  const [isHQ] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      return user?.role === 'admin'
    } catch { return false }
  })

  useEffect(() => {
    fetchSettlements()
  }, [filter, institutionId])

  const fetchSettlements = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filter.status) params.append('status', filter.status)
      // 如果不是总部管理员，传递机构ID
      if (!isHQ && institutionId) {
        params.append('store_id', institutionId)
      }
      params.append('pageSize', '100')
      
      const res = await fetch(`${API}/store-settlements?${params}`)
      const json = await res.json()
      if (json.success) {
        setSettlements(json.data || [])
        setStats(json.stats || {})
      }
    } catch (e) {
      console.error('获取结算列表失败:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  const handleSelectAll = () => {
    const pendingIds = settlements.filter(s => s.status === 'pending').map(s => s.id)
    if (selectedIds.length === pendingIds.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(pendingIds)
    }
  }

  const handleBatchPay = async () => {
    if (selectedIds.length === 0) {
      alert('请选择要结算的记录')
      return
    }
    if (!confirm(`确认结算 ${selectedIds.length} 条记录？`)) return

    try {
      const res = await fetch(`${API}/store-settlements/batch-pay`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds })
      })
      const json = await res.json()
      if (json.success) {
        alert(`已结算 ${json.updated} 条记录`)
        setSelectedIds([])
        fetchSettlements()
      } else {
        alert(json.error || '结算失败')
      }
    } catch (e) {
      alert('结算失败')
    }
  }

  const handleSinglePay = async (id) => {
    if (!confirm('确认完成结算？')) return
    try {
      const res = await fetch(`${API}/store-settlements/${id}/pay`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      })
      const json = await res.json()
      if (json.success) {
        alert('结算已完成')
        fetchSettlements()
      } else {
        alert(json.error || '结算失败')
      }
    } catch (e) {
      alert('结算失败')
    }
  }

  const formatMoney = (amount) => {
    return `¥${parseFloat(amount || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('zh-CN')
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0 }}>💰 门店结算</h2>
          <p style={{ margin: '6px 0 0', color: '#999', fontSize: 13 }}>
            平台抽成10%，结算90%给门店
            {institutionId && <span style={{ color: '#1890ff', marginLeft: 8 }}>（机构ID: {institutionId}）</span>}
          </p>
        </div>
        
        {selectedIds.length > 0 && (
          <button 
            onClick={handleBatchPay}
            style={{ 
              padding: '10px 24px', 
              background: '#52c41a', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            批量结算 ({selectedIds.length})
          </button>
        )}
      </div>

      {/* 统计卡片 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: 16 }}>
          <div style={{ color: '#52c41a', fontSize: 13 }}>待结算金额</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>{formatMoney(stats.pending_amount)}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{stats.pending_count || 0} 笔</div>
        </div>
        <div style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 8, padding: 16 }}>
          <div style={{ color: '#1890ff', fontSize: 13 }}>已结算金额</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>{formatMoney(stats.paid_amount)}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{stats.paid_count || 0} 笔</div>
        </div>
        <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: 16 }}>
          <div style={{ color: '#fa8c16', fontSize: 13 }}>门店总金额</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fa8c16' }}>{formatMoney(stats.store_amount)}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{stats.total_count || 0} 笔</div>
        </div>
        <div style={{ background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 8, padding: 16 }}>
          <div style={{ color: '#722ed1', fontSize: 13 }}>平台抽成(10%)</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#722ed1' }}>{formatMoney(stats.platform_fee)}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>总收入</div>
        </div>
      </div>

      {/* 筛选 */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
        <select 
          value={filter.status} 
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d9d9d9' }}
        >
          <option value="">全部状态</option>
          <option value="pending">待结算</option>
          <option value="paid">已结算</option>
        </select>
      </div>

      {/* 表格 */}
      <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
              <th style={{ padding: '12px 8px', textAlign: 'center', width: 40 }}>
                <input 
                  type="checkbox" 
                  checked={settlements.filter(s => s.status === 'pending').length > 0 && selectedIds.length === settlements.filter(s => s.status === 'pending').length}
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ padding: 12, textAlign: 'left' }}>产品名称</th>
              <th style={{ padding: 12, textAlign: 'left' }}>客户</th>
              <th style={{ padding: 12, textAlign: 'right' }}>订单金额</th>
              <th style={{ padding: 12, textAlign: 'right' }}>平台抽成(10%)</th>
              <th style={{ padding: 12, textAlign: 'right' }}>门店结算(90%)</th>
              <th style={{ padding: 12, textAlign: 'center' }}>状态</th>
              <th style={{ padding: 12, textAlign: 'center' }}>核销时间</th>
              <th style={{ padding: 12, textAlign: 'center' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {settlements.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                  暂无结算记录
                </td>
              </tr>
            ) : settlements.map((item, index) => (
              <tr key={item.id} style={{ borderBottom: index < settlements.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  {item.status === 'pending' ? (
                    <input 
                      type="checkbox" 
                      checked={selectedIds.includes(item.id)}
                      onChange={() => handleSelect(item.id)}
                    />
                  ) : (
                    <span style={{ color: '#d9d9d9' }}>✓</span>
                  )}
                </td>
                <td style={{ padding: 12 }}>
                  <div style={{ fontWeight: 500 }}>{item.product_name}</div>
                  <div style={{ fontSize: 12, color: '#999' }}>订单号: {item.order_id}</div>
                </td>
                <td style={{ padding: 12 }}>{item.customer_name || '-'}</td>
                <td style={{ padding: 12, textAlign: 'right', fontWeight: 500 }}>
                  {formatMoney(item.total_amount)}
                </td>
                <td style={{ padding: 12, textAlign: 'right', color: '#722ed1' }}>
                  {formatMoney(item.platform_fee)}
                </td>
                <td style={{ padding: 12, textAlign: 'right', color: '#52c41a', fontWeight: 500 }}>
                  {formatMoney(item.store_amount)}
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  <span style={{ 
                    padding: '4px 8px', 
                    borderRadius: 4,
                    fontSize: 12,
                    background: item.status === 'pending' ? '#fff7e6' : '#f6ffed',
                    color: item.status === 'pending' ? '#fa8c16' : '#52c41a'
                  }}>
                    {item.status === 'pending' ? '待结算' : '已结算'}
                  </span>
                </td>
                <td style={{ padding: 12, textAlign: 'center', fontSize: 12, color: '#666' }}>
                  {formatDate(item.verified_at)}
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  {item.status === 'pending' ? (
                    <button
                      onClick={() => handleSinglePay(item.id)}
                      style={{ 
                        padding: '4px 12px', 
                        background: '#52c41a', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      确认结算
                    </button>
                  ) : (
                    <span style={{ fontSize: 12, color: '#999' }}>
                      {formatDate(item.paid_at)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16, fontSize: 12, color: '#999' }}>
        说明：线下产品（生日派对、绘本剧场创作营等）核销后，系统自动生成结算记录。
        平台抽取10%作为抽成，剩余90%结算给服务门店。
      </div>
    </div>
  )
}
